import { useEffect, useState } from "react";
import axios from "axios";
import {
  X, Users, MessageSquare, TrendingUp, Sparkles, Loader2, ExternalLink,
  ArrowUpRight, Zap,
} from "lucide-react";
import { API } from "@/lib/api";

const NEON = "#22C55E";
const GOLD = "#F5B800";
const BLUE = "#4CC3FF";
const PANEL = "#0F0F13";
const LINE = "rgba(255,255,255,0.10)";
const DIM = "rgba(255,255,255,0.7)";
const FADE = "rgba(255,255,255,0.4)";

// Real tenants have live data; concept-only shows an empty/demo state.
const TENANT_SLUGS = new Set(["lonestar", "plugitai"]);

export default function CompanyAnalyticsDrawer({ company, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const querySlug = TENANT_SLUGS.has(company.slug) ? company.slug : company.slug;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/public/hub/company-stats`, { params: { slug: querySlug } });
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) setError("Couldn't load stats. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [querySlug]);

  const isConceptOnly = stats && !stats.has_tenant;

  return (
    <div className="fixed inset-0 z-50" data-testid="analytics-overlay">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="analytics-backdrop"
      />

      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[500px] md:w-[560px] flex flex-col animate-slideIn overflow-hidden"
        style={{
          background: "#08080B",
          borderLeft: `1px solid ${LINE}`,
          boxShadow: `-20px 0 60px -20px rgba(0,0,0,0.7), inset 4px 0 30px -8px ${GOLD}22`,
        }}
        data-testid="analytics-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: LINE }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                 style={{ background: PANEL, border: `1px solid ${GOLD}55` }}>
              <TrendingUp size={16} style={{ color: GOLD }} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: FADE }}>
                Toolkit · Live analytics
              </div>
              <div className="font-black text-sm text-white truncate" data-testid="analytics-company">
                {company.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-white/5 shrink-0"
            style={{ color: DIM }}
            data-testid="analytics-close"
          >
            <X size={16} />
          </button>
        </div>

        {isConceptOnly && !loading && (
          <div className="px-5 py-2 text-[10px] uppercase tracking-[0.22em] border-b flex items-center gap-2"
               style={{ background: "rgba(245,184,0,0.06)", borderColor: LINE, color: "rgba(245,184,0,0.9)" }}>
            <Sparkles size={12} />
            Concept build · no live data yet
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5" data-testid="analytics-body">
          {loading && (
            <div className="flex items-center justify-center py-16" data-testid="analytics-loading">
              <Loader2 size={20} className="animate-spin" style={{ color: GOLD }} />
            </div>
          )}

          {!loading && error && (
            <div className="text-sm rounded-md px-3 py-2 border"
                 style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.4)", color: "#FCA5A5" }}
                 data-testid="analytics-error">
              {error}
            </div>
          )}

          {!loading && !error && stats && (
            <>
              {/* Primary tiles */}
              <div className="grid grid-cols-3 gap-3">
                <Tile icon={Users} label="Leads (total)" value={stats.leads_total} tint={NEON} testId="analytics-leads-total" />
                <Tile icon={MessageSquare} label="Chats today" value={stats.conversations_today} tint={BLUE} testId="analytics-chats-today" />
                <Tile icon={Zap} label="Msgs today" value={stats.messages_today} tint="#A78BFA" testId="analytics-messages-today" />
              </div>

              {/* Secondary row */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Tile label="New (unhandled)" value={stats.leads_new} tint={GOLD} testId="analytics-leads-new" small />
                <Tile label="This week" value={stats.leads_this_week} tint="#FFF" testId="analytics-leads-week" small />
                <Tile label="Conversations" value={stats.conversations_total} tint="#FFF" testId="analytics-convos-total" small />
              </div>

              {/* Latest AI score */}
              <div className="mt-6 rounded-lg border p-4"
                   style={{ background: PANEL, borderColor: LINE }}
                   data-testid="analytics-score-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-black" style={{ color: FADE }}>
                    Conversation intelligence
                  </div>
                  {stats.graded_count > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: FADE }}>
                      {stats.graded_count} graded
                    </div>
                  )}
                </div>

                {stats.latest_score == null ? (
                  <div className="text-sm py-2" style={{ color: DIM }}>
                    No conversations graded yet.
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3">
                      <div className="font-black leading-none"
                           style={{ fontSize: "3rem", color: scoreColor(stats.latest_score), textShadow: `0 0 24px ${scoreColor(stats.latest_score)}66` }}
                           data-testid="analytics-latest-score">
                        {stats.latest_score}
                      </div>
                      <div className="text-sm" style={{ color: DIM }}>/ 100 · latest</div>
                    </div>
                    <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <div className="h-full transition-all"
                           style={{ width: `${stats.latest_score}%`, background: scoreColor(stats.latest_score), boxShadow: `0 0 12px ${scoreColor(stats.latest_score)}` }} />
                    </div>
                    {stats.avg_score != null && (
                      <div className="mt-3 text-[11px]" style={{ color: FADE }}>
                        Average across {stats.graded_count} graded: <span style={{ color: "#FFF", fontWeight: 800 }}>{stats.avg_score}</span> / 100
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Recent leads */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-black" style={{ color: FADE }}>
                    Recent leads
                  </div>
                  {stats.recent_leads.length > 0 && (
                    <a href="/admin/leads"
                       target="_blank"
                       rel="noreferrer"
                       className="text-[10px] uppercase tracking-[0.22em] hover:text-white inline-flex items-center gap-1"
                       style={{ color: DIM }}
                       data-testid="analytics-view-all-leads">
                      View all <ArrowUpRight size={11} />
                    </a>
                  )}
                </div>
                {stats.recent_leads.length === 0 ? (
                  <div className="text-sm rounded-lg border px-4 py-6 text-center"
                       style={{ background: PANEL, borderColor: LINE, color: FADE }}>
                    No leads captured yet.
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden" style={{ borderColor: LINE }}>
                    {stats.recent_leads.map((l, i) => (
                      <div key={l.id}
                           className="px-4 py-3 flex items-center justify-between"
                           style={{ background: i % 2 ? PANEL : "rgba(15,15,19,0.5)", borderTop: i === 0 ? "none" : `1px solid ${LINE}` }}
                           data-testid={`analytics-lead-${l.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-black text-white truncate">{l.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] mt-0.5" style={{ color: FADE }}>
                            {l.source || "—"} · {formatWhen(l.created_at)}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.2em] font-black px-2 py-1 rounded"
                              style={{ background: statusBg(l.status), color: statusColor(l.status), border: `1px solid ${statusBorder(l.status)}` }}>
                          {l.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick actions */}
              <div className="mt-6 grid grid-cols-2 gap-2">
                {company.url && (
                  <a href={company.url}
                     target="_blank"
                     rel="noreferrer"
                     className="inline-flex items-center justify-center gap-2 h-10 rounded-md border text-[10px] uppercase tracking-[0.22em] font-black hover:text-white"
                     style={{ borderColor: LINE, color: DIM }}
                     data-testid="analytics-open-site">
                    <ExternalLink size={12} /> Open live site
                  </a>
                )}
                <a href="/admin/conversations"
                   target="_blank"
                   rel="noreferrer"
                   className="inline-flex items-center justify-center gap-2 h-10 rounded-md text-[10px] uppercase tracking-[0.22em] font-black"
                   style={{ background: GOLD, color: "#0A0A0C" }}
                   data-testid="analytics-open-intel">
                  Open Intel Console →
                </a>
              </div>
            </>
          )}
        </div>

        <style>{`
          @keyframes slideIn { from { transform: translateX(100%);} to { transform: translateX(0);} }
          .animate-slideIn { animation: slideIn 260ms cubic-bezier(0.16,1,0.3,1) both; }
        `}</style>
      </aside>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tint, testId, small }) {
  return (
    <div className="rounded-lg border p-3" style={{ background: PANEL, borderColor: LINE }} data-testid={testId}>
      {Icon && <Icon size={14} style={{ color: tint }} />}
      <div className={`font-black leading-none mt-${Icon ? 2 : 0}`}
           style={{ fontSize: small ? "1.4rem" : "1.8rem", color: tint, textShadow: `0 0 18px ${tint}44` }}>
        {value ?? "—"}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] mt-1.5" style={{ color: FADE }}>
        {label}
      </div>
    </div>
  );
}

function scoreColor(v) {
  if (v >= 85) return NEON;
  if (v >= 70) return "#4CC3FF";
  if (v >= 55) return GOLD;
  return "#EF4444";
}
function statusBg(s) {
  return { new: "rgba(34,197,94,0.10)", contacted: "rgba(76,195,255,0.10)", qualified: "rgba(245,184,0,0.10)", won: "rgba(34,197,94,0.18)", lost: "rgba(220,38,38,0.10)" }[s] || "rgba(255,255,255,0.05)";
}
function statusColor(s) {
  return { new: NEON, contacted: "#4CC3FF", qualified: GOLD, won: NEON, lost: "#F87171" }[s] || "#FFF";
}
function statusBorder(s) {
  return { new: `${NEON}66`, contacted: "rgba(76,195,255,0.5)", qualified: `${GOLD}66`, won: NEON, lost: "rgba(220,38,38,0.5)" }[s] || LINE;
}
function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}
